import React, { useMemo } from 'react';
import { Box, Chip, Link, CircularProgress, LinearProgress, Step, StepLabel, Stepper, Tooltip, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CustomStepIcon from '../components/CustomStepIcon';
import type { StepCompletionStatus } from './useStepCompletionStatus';

export interface GenericWizardSubStep {
  id: string;
  title: string;
  /**
   * Renders as a non-interactive label that groups the substeps below it (e.g. one per draw in a
   * multi-draw Magic Report) instead of a completable step. Excluded from progress totals.
   */
  isGroupHeader?: boolean;
}

export interface GenericWizardStep {
  id: string;
  title: string;
  subSteps?: GenericWizardSubStep[];
}

interface GenericWizardStepperProps {
  steps: GenericWizardStep[];
  activeStep: number;
  isSaving: boolean;
  activeSubStepId: string | null;
  isScrollSpyStep: (stepId: string) => boolean;
  getCompletionStatusForStep: (stepIndex: number) => StepCompletionStatus;
  onStepClick: (index: number) => Promise<void>;
  onSubStepClick: (stepIndex: number, subStepId: string, isStepActive: boolean) => Promise<void>;
  getSubStepRank?: (subStepId: string) => number | undefined;
  getSubStepCompleted?: (subStepId: string) => boolean;
  excludeFromOverallProgress?: (stepIndex: number, subStepId?: string) => boolean;
  collapsed?: boolean;
  getViewOnlyLinkForStep?: (stepId: string) => string | undefined;
}

export const GenericWizardStepper: React.FC<GenericWizardStepperProps> = ({
  steps,
  activeStep,
  isSaving,
  activeSubStepId,
  isScrollSpyStep,
  getCompletionStatusForStep,
  onStepClick,
  onSubStepClick,
  getSubStepRank,
  getSubStepCompleted,
  excludeFromOverallProgress,
  collapsed = false,
  getViewOnlyLinkForStep,
}) => {
  const trackableUnits = useMemo(
    () =>
      steps.flatMap((step, index) => {
        if (step.subSteps && step.subSteps.length > 0) {
          return step.subSteps
            .filter(subStep => !subStep.isGroupHeader && !excludeFromOverallProgress?.(index, subStep.id))
            .map(subStep => getSubStepCompleted?.(subStep.id) ?? false);
        }
        return excludeFromOverallProgress?.(index) ? [] : [getCompletionStatusForStep(index).isComplete];
      }),
    [steps, excludeFromOverallProgress, getSubStepCompleted, getCompletionStatusForStep],
  );
  const completedUnitCount = useMemo(() => trackableUnits.filter(Boolean).length, [trackableUnits]);
  const overallProgress = trackableUnits.length > 0 ? (completedUnitCount / trackableUnits.length) * 100 : 0;

  if (collapsed) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', minHeight: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            flexGrow: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            width: '100%',
            scrollbarWidth: 'thin',
            scrollbarGutter: 'stable',
            '&::-webkit-scrollbar': {
              width: 6,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'action.disabled',
              borderRadius: 999,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
          }}
        >
          {steps.map((step, index) => {
            const completionStatus = getCompletionStatusForStep(index);
            const isActive = index === activeStep;
            const viewOnlyLink = getViewOnlyLinkForStep?.(step.id);
            return (
              <Box key={step.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', mb: 1.5 }}>
                <Tooltip
                  title={
                    viewOnlyLink ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {step.title}
                        <Link
                          href={viewOnlyLink}
                          target='_blank'
                          rel='noopener noreferrer'
                          onClick={e => e.stopPropagation()}
                          sx={{ display: 'inline-flex', alignItems: 'center', color: 'inherit' }}
                          aria-label='View photos in new tab (read-only)'
                        >
                          <OpenInNewIcon fontSize='inherit' sx={{ fontSize: '1rem' }} />
                        </Link>
                      </Box>
                    ) : (
                      step.title
                    )
                  }
                  placement='right'
                >
                  <Box
                    role='button'
                    tabIndex={0}
                    onClick={async () => {
                      if (isSaving) return;
                      try {
                        await onStepClick(index);
                      } catch (error) {
                        console.error('Error navigating to step:', error);
                      }
                    }}
                    onKeyDown={async e => {
                      if (e.key !== 'Enter' && e.key !== ' ') return;
                      e.preventDefault();
                      if (isSaving) return;
                      try {
                        await onStepClick(index);
                      } catch (error) {
                        console.error('Error navigating to step:', error);
                      }
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 0.5,
                      cursor: isSaving ? 'not-allowed' : 'pointer',
                      opacity: isSaving ? 0.6 : 1,
                      borderRadius: '50%',
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: '50%',
                        boxShadow: isActive ? theme => `0 0 0 2px ${theme.palette.primary.main}` : 'none',
                        display: 'flex',
                      }}
                    >
                      <CustomStepIcon icon={index + 1} completionStatus={completionStatus} active={isActive} />
                    </Box>
                  </Box>
                </Tooltip>

                {step.subSteps && step.subSteps.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                    {step.subSteps.map(subStep => {
                      const isSubStepCompleted = getSubStepCompleted ? getSubStepCompleted(subStep.id) : false;
                      const isActiveSubStep = isScrollSpyStep(step.id) && activeSubStepId === subStep.id;
                      // Group headers carry no state of their own; a divider keeps the collapsed
                      // rail readable without pretending to be a completable step.
                      if (subStep.isGroupHeader) {
                        return (
                          <Tooltip key={subStep.id} title={subStep.title} placement='right'>
                            <Box sx={{ width: 12, height: 1, bgcolor: 'divider', my: 0.5 }} />
                          </Tooltip>
                        );
                      }
                      return (
                        <Tooltip key={subStep.id} title={subStep.title} placement='right'>
                          <Box
                            onClick={async (e: React.MouseEvent) => {
                              e.stopPropagation();
                              if (isSaving) return;
                              try {
                                await onSubStepClick(index, subStep.id, isActive);
                              } catch (error) {
                                console.error('Error navigating to substep:', error);
                              }
                            }}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 16,
                              height: 16,
                              cursor: isSaving ? 'not-allowed' : 'pointer',
                              opacity: isSaving ? 0.6 : 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                border: isSubStepCompleted || isActiveSubStep ? 'none' : '1.5px solid',
                                borderColor: 'divider',
                                bgcolor: isSubStepCompleted ? 'success.main' : isActiveSubStep ? 'primary.main' : 'transparent',
                              }}
                            />
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        <Tooltip title={`Overall progress: ${completedUnitCount}/${trackableUnits.length}`} placement='right'>
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              mt: 1,
              mb: 1,
              flexShrink: 0,
            }}
          >
            <CircularProgress
              variant='determinate'
              value={100}
              size={36}
              thickness={4}
              sx={{ color: 'action.hover', position: 'absolute' }}
            />
            <CircularProgress
              variant='determinate'
              value={overallProgress}
              size={36}
              thickness={4}
              sx={{ color: overallProgress === 100 ? 'success.main' : 'primary.main', position: 'absolute' }}
            />
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.secondary' }}>{Math.round(overallProgress)}%</Typography>
          </Box>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Stepper
        activeStep={activeStep}
        orientation='vertical'
        sx={{
          flexGrow: 1,
          '& .MuiStepConnector-root': {
            marginLeft: '11px',
            display: 'flex',
          },
          '& .MuiStepConnector-line': {
            borderColor: 'divider',
            borderLeftWidth: 1,
            minHeight: 6,
            flex: 1,
          },
          '& .MuiStepLabel-iconContainer': {
            alignSelf: 'flex-start',
            mt: 0.125,
          },
          '& .MuiStep-root': {
            pb: 0,
          },
        }}
      >
        {steps.map((step, index) => {
          const completionStatus = getCompletionStatusForStep(index);
          const isActive = index === activeStep;
          const scrollSpyEnabled = isScrollSpyStep(step.id);
          const viewOnlyLink = getViewOnlyLinkForStep?.(step.id);

          return (
            <Step key={step.id} completed={false}>
              <StepLabel
                onClick={async () => {
                  if (isSaving) return;
                  try {
                    await onStepClick(index);
                  } catch (error) {
                    console.error('Error navigating to step:', error);
                  }
                }}
                sx={{
                  cursor: isSaving ? 'not-allowed' : 'pointer',
                  opacity: isSaving ? 0.6 : 1,
                  pointerEvents: isSaving ? 'none' : 'auto',
                  borderRadius: 1.5,
                  mx: -1,
                  px: 1,
                  py: 0.375,
                  transition: 'background-color 0.15s ease',
                  '&:hover': {
                    bgcolor: isSaving ? 'transparent' : 'action.hover',
                  },
                  '.MuiStepLabel-label': {
                    fontWeight: isActive ? 700 : 500,
                    fontSize: '0.95rem',
                    lineHeight: 1.3,
                    color: isActive ? 'text.primary' : 'text.secondary',
                  },
                }}
                StepIconComponent={props => <CustomStepIcon {...props} icon={index + 1} completionStatus={completionStatus} />}
              >
                <Box>
                  <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    {step.title}
                    {viewOnlyLink && (
                      <Tooltip title='View photos in new tab (read-only)'>
                        <Link
                          href={viewOnlyLink}
                          target='_blank'
                          rel='noopener noreferrer'
                          onClick={e => e.stopPropagation()}
                          sx={{ display: 'inline-flex', alignItems: 'center', color: 'primary.main' }}
                          aria-label='View photos in new tab (read-only)'
                        >
                          <OpenInNewIcon fontSize='inherit' sx={{ fontSize: '1rem' }} />
                        </Link>
                      </Tooltip>
                    )}
                  </Box>
                  {completionStatus.hasWarning && completionStatus.warningMessage && (
                    <Typography variant='caption' display='block' sx={{ color: 'warning.dark', fontSize: 12, mt: 0.25 }}>
                      {completionStatus.warningMessage}
                    </Typography>
                  )}

                  {step.subSteps && step.subSteps.length > 0 && (
                    <Box
                      sx={{
                        mt: 1.5,
                        pl: scrollSpyEnabled ? 0 : 3,
                        ml: scrollSpyEnabled ? '-25px' : 0,
                        pr: 1,
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                      }}
                    >
                      {step.subSteps.map((subStep, subIndex) => {
                        const isLastSub = subIndex === step.subSteps!.length - 1;
                        const isActiveSubStep = scrollSpyEnabled && activeSubStepId === subStep.id;
                        const subStepRank = getSubStepRank ? getSubStepRank(subStep.id) : undefined;
                        const isSubStepCompleted = getSubStepCompleted ? getSubStepCompleted(subStep.id) : false;

                        if (subStep.isGroupHeader) {
                          return (
                            <Typography
                              key={subStep.id}
                              sx={{
                                fontSize: 12,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                                color: isActive ? 'text.primary' : 'text.disabled',
                                mt: subIndex === 0 ? 0 : 1.25,
                                mb: 0.75,
                                overflowWrap: 'break-word',
                              }}
                            >
                              {subStep.title}
                            </Typography>
                          );
                        }

                        return (
                          <Box
                            key={subStep.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 0.75,
                              mb: isLastSub ? 0 : 0.75,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                width: 12,
                                flexShrink: 0,
                              }}
                            >
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                }}
                              >
                                {isSubStepCompleted ? (
                                  <CheckIcon sx={{ fontSize: 12, color: 'success.main' }} />
                                ) : (
                                  <Box
                                    sx={{
                                      width: 7,
                                      height: 7,
                                      borderRadius: '50%',
                                      border: isActiveSubStep ? 'none' : '1.5px solid',
                                      borderColor: isActive ? 'primary.light' : 'divider',
                                      bgcolor: isActiveSubStep ? 'primary.main' : 'transparent',
                                    }}
                                  />
                                )}
                              </Box>
                              {!isLastSub && (
                                <Box
                                  sx={{
                                    width: '1px',
                                    minHeight: 14,
                                    height: '100%',
                                    mt: 0.25,
                                    bgcolor: 'divider',
                                  }}
                                />
                              )}
                            </Box>

                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box
                                onClick={async (e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  if (isSaving) return;
                                  try {
                                    await onSubStepClick(index, subStep.id, isActive);
                                  } catch (error) {
                                    console.error('Error navigating to substep:', error);
                                  }
                                }}
                                sx={{
                                  cursor: isSaving ? 'not-allowed' : 'pointer',
                                  opacity: isSaving ? 0.6 : 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  flexWrap: 'wrap',
                                  borderRadius: 1,
                                  px: 0.5,
                                  mx: -0.5,
                                  transition: 'background-color 0.15s ease',
                                  '&:hover': {
                                    bgcolor: isSaving ? 'transparent' : 'action.hover',
                                  },
                                }}
                              >
                                <Typography
                                  className='substep-title'
                                  sx={{
                                    fontSize: 13,
                                    lineHeight: 1.35,
                                    overflowWrap: 'break-word',
                                    wordBreak: 'break-word',
                                    fontWeight: isActiveSubStep ? 600 : 400,
                                    color: isActiveSubStep ? 'primary.main' : isActive ? 'text.secondary' : 'text.disabled',
                                  }}
                                >
                                  {subStep.title}
                                </Typography>
                                {subStepRank !== undefined && (
                                  <Chip
                                    label={`#${subStepRank}`}
                                    size='small'
                                    color='primary'
                                    variant='outlined'
                                    sx={{ height: 16, fontSize: '0.6rem', px: 0.25, flexShrink: 0 }}
                                  />
                                )}
                              </Box>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </StepLabel>
            </Step>
          );
        })}
      </Stepper>

      <Box sx={{ pt: 1.25, mt: 1, borderTop: theme => `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.75 }}>
          <Typography variant='caption' sx={{ fontWeight: 700, fontSize: 12.5, color: 'text.primary' }}>
            Overall progress
          </Typography>
          <Typography variant='caption' sx={{ fontWeight: 600, fontSize: 12, color: 'text.secondary' }}>
            {completedUnitCount}/{trackableUnits.length}
          </Typography>
        </Box>
        <LinearProgress
          variant='determinate'
          value={overallProgress}
          sx={{
            height: 6,
            borderRadius: 999,
            bgcolor: 'action.hover',
            '& .MuiLinearProgress-bar': {
              borderRadius: 999,
              bgcolor: overallProgress === 100 ? 'success.main' : 'primary.main',
            },
          }}
        />
      </Box>
    </Box>
  );
};
